import { lazy } from "react";
import { Redirect, useParams } from "react-router-dom";
import ReleaseWrapper from "@/pages/models/release/ReleaseWrapper";
import { Roles } from "@/auth/permissions";
import Icon from "@imminently/decisively-ui/Icon";

import { MonitorProjectScope } from "@/common";
import { Sessions } from "@/pages/sessions";
import { Contacts } from "@/pages/contacts";

const DecisionDashboard = lazy(() => import("@/pages/decisions/DecisionDashboard"));
const MonitorFiles = lazy(() => import("@/pages/monitor/FileProcessingMonitor/FileProcessingMonitorPage"));
const MonitorEvents = lazy(() => import("@/pages/events/Events").then((module) => ({ default: module.Events })));
const OrgModelsMonitor = lazy(() => import("@/pages/monitor/OrgModels/OrgModelsPage"));
const ExtensionsMonitor = lazy(
  () => import("@/pages/monitor/Extensions/ExtensionsPage"),
);
const ContactIdentifiersMonitor = lazy(() => import("@/pages/monitor/ContactIdentifiers/ContactIdentifiersPage"));

const MonitorOverviewPage = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  return (
    <ReleaseWrapper
      title="Monitor"
      scopeComponent={MonitorProjectScope}
    >
      <Sessions activeSessionId={sessionId} />
    </ReleaseWrapper>
  );
};

export const monitor = [
  {
    path: "/monitor",
    layout: "decisively-scoped",
    role: Roles.ReportViewer,
    children: [
      {
        path: ["/monitor/overview", "/monitor/overview/:sessionId"],
        exact: true,
        role: Roles.ReportViewer,
        component: MonitorOverviewPage,
        sidebar: {
          name: "Monitor",
          icon: <Icon name="citizenship" />,
        }
      },
      {
        path: "/monitor/failedInteractions",
        exact: true,
        role: Roles.ReportViewer,
        component: () => <Redirect to="/monitor/events?tab=overview&eventType=session.failedInteraction" />,
      },
      {
        // TODO for some reason crashes if this url changes
        path: "/monitor",
        exact: true,
        role: Roles.ReportViewer,
        sidebar: { name: "Reports", icon: <Icon name="testing" /> },
        component: () => (
          <ReleaseWrapper
            title='Reports'
            scopeComponent={MonitorProjectScope}
          >
            <DecisionDashboard />
          </ReleaseWrapper>
        ),
      },
      {
        path: "/monitor/events",
        exact: true,
        role: [Roles.ReportViewer, Roles.TimesheetAdmin, Roles.SuperAdmin, Roles.Owner],
        component: () => (
          <ReleaseWrapper
            title="Events"
            scopeComponent={MonitorProjectScope}
          >
            <MonitorEvents source="model" />
          </ReleaseWrapper>
        ),
        sidebar: {
          name: "Events",
          icon: <Icon name="trigger" />,
        }
      },
      {
        path: "/monitor/files",
        exact: true,
        role: [Roles.ReportViewer, Roles.TimesheetAdmin, Roles.SuperAdmin, Roles.Owner],
        component: () => (
          <ReleaseWrapper title="Files" scopeComponent={MonitorProjectScope}>
            <MonitorFiles filesOnly />
          </ReleaseWrapper>
        ),
        sidebar: { name: "Files", icon: <Icon name="file" /> },
      },
      {
        path: "/monitor/file-processing",
        exact: true,
        role: Roles.ReportViewer,
        component: () => <Redirect to="/monitor/events?tab=events&group=fileProcessing" />,
      },
      {
        path: "/monitor/contacts",
        exact: true,
        role: [Roles.ContactsReadAll, Roles.ContactsWriter],
        component: () => (
          <ReleaseWrapper
            title='Contacts'
            scopeComponent={MonitorProjectScope}
          >
            <Contacts />
          </ReleaseWrapper>
        ),
        sidebar: {
          name: "Contacts",
          icon: <Icon name="users" />,
        },
        group: {
          name: "Contacts",
          icon: <Icon name="users" />,
          open: true,
        },
      },
      {
        path: "/monitor/contacts/extensions",
        exact: true,
        role: Roles.ReportViewer,
        component: () => (
          <ReleaseWrapper
            title="Extensions"
            scopeComponent={MonitorProjectScope}
          >
            <ExtensionsMonitor />
          </ReleaseWrapper>
        ),
        sidebar: {
          name: "Extensions",
          icon: <Icon name="database" />,
        },
        group: {
          name: "Contacts",
          icon: <Icon name="users" />,
          open: true,
        },
      },
      {
        path: "/monitor/contacts/identifiers",
        exact: true,
        role: [Roles.ContactsReadAll, Roles.ContactsWriter],
        component: () => (
          <ReleaseWrapper
            title="Contact Identifiers"
            scopeComponent={MonitorProjectScope}
          >
            <ContactIdentifiersMonitor />
          </ReleaseWrapper>
        ),
        sidebar: {
          name: "Contact Identifiers",
          icon: <Icon name="relationship" />,
        },
        group: {
          name: "Contacts",
          icon: <Icon name="users" />,
          open: true,
        },
      },
      {
        path: "/monitor/contacts/org-models",
        exact: true,
        role: [Roles.ContactsReadAll, Roles.ContactsWriter],
        component: () => (
          <ReleaseWrapper
            title="Org Models"
            scopeComponent={MonitorProjectScope}
          >
            <OrgModelsMonitor />
          </ReleaseWrapper>
        ),
        sidebar: {
          name: "Org Models",
          icon: <Icon name="split" />,
        },
        group: {
          name: "Contacts",
          icon: <Icon name="users" />,
          open: true,
        },
      },
    ]
  },
];
